import React, { useState, useEffect } from 'react';
import { getAllClients } from '../utils/apiService';
import { calculateClientCompletion, getCompletionBadge } from '../utils/completion/clientCompletionCalculator';
import { Link } from 'react-router-dom';
import { ExclamationTriangleIcon, ServerIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import ServerCompletionDetails from '../components/completion/ServerCompletionDetails';

const CompletionDashboard = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('server_issues'); // 'completion', 'name', 'alerts', 'server_issues'
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'complete', 'incomplete', 'critical', 'server_incomplete'

  useEffect(() => {
    const loadClients = async () => {
      setLoading(true);
      const data = await getAllClients();
      setClients(data);
      setLoading(false);
    };
    loadClients();
  }, []);

  // Calculer la complétion pour chaque client
  const clientsWithCompletion = clients.map(client => {
    const completion = calculateClientCompletion(client);
    return {
      ...client,
      completion
    };
  });

  // Filtrer les clients
  const filteredClients = clientsWithCompletion.filter(client => {
    switch (filterStatus) {
      case 'complete':
        return client.completion.percentage === 100;
      case 'incomplete':
        return client.completion.percentage < 100;
      case 'critical':
        return client.completion.details.hasNoProbe || client.completion.details.hasUnoptimizedDB;
      case 'server_incomplete':
        return client.completion.serverAnalysis?.incompleteServers?.length > 0;
      default:
        return true;
    }
  });

  // Trier les clients
  const sortedClients = [...filteredClients].sort((a, b) => {
    switch (sortBy) {
      case 'completion':
        return a.completion.percentage - b.completion.percentage;
      case 'name':
        return a.name.localeCompare(b.name);
      case 'alerts':
        const alertsA = (a.completion.details.hasNoProbe ? 2 : 0) + (a.completion.details.hasUnoptimizedDB ? 1 : 0);
        const alertsB = (b.completion.details.hasNoProbe ? 2 : 0) + (b.completion.details.hasUnoptimizedDB ? 1 : 0);
        return alertsB - alertsA;
      case 'server_issues':
        const serverIssuesA = (a.completion.serverAnalysis?.incompleteServers?.length || 0) + (a.completion.serverAnalysis?.criticalIssues?.length || 0);
        const serverIssuesB = (b.completion.serverAnalysis?.incompleteServers?.length || 0) + (b.completion.serverAnalysis?.criticalIssues?.length || 0);
        return serverIssuesB - serverIssuesA;
      default:
        return 0;
    }
  });

  // Statistiques globales
  const stats = {
    total: clients.length,
    complete: clientsWithCompletion.filter(c => c.completion.percentage === 100).length,
    incomplete: clientsWithCompletion.filter(c => c.completion.percentage < 100).length,
    withNoProbe: clientsWithCompletion.filter(c => c.completion.details.hasNoProbe).length,
    withUnoptimizedDB: clientsWithCompletion.filter(c => c.completion.details.hasUnoptimizedDB).length,
    withIncompleteServers: clientsWithCompletion.filter(c => c.completion.serverAnalysis?.incompleteServers?.length > 0).length,
    totalIncompleteServers: clientsWithCompletion.reduce((sum, c) => sum + (c.completion.serverAnalysis?.incompleteServers?.length || 0), 0),
    averageCompletion: Math.round(
      clientsWithCompletion.reduce((sum, c) => sum + c.completion.percentage, 0) / (clientsWithCompletion.length || 1)
    )
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <h1 className="text-3xl font-bold mb-6">Tableau de bord de complétion</h1>

      {/* Cartes de statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-gray-800">{stats.total}</div>
          <div className="text-sm text-gray-600">Clients total</div>
        </div>
        
        <div className="bg-green-50 border border-green-200 rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-green-600">{stats.complete}</div>
          <div className="text-sm text-green-700">Complets (100%)</div>
          <div className="text-xs text-green-600 mt-1">{Math.round(stats.complete / stats.total * 100)}%</div>
        </div>
        
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-yellow-600">{stats.incomplete}</div>
          <div className="text-sm text-yellow-700">Incomplets</div>
          <div className="text-xs text-yellow-600 mt-1">{Math.round(stats.incomplete / stats.total * 100)}%</div>
        </div>
        
        <div className="bg-amber-50 border border-amber-200 rounded-lg shadow p-4">
          <div className="flex items-center mb-1">
            <ServerIcon className="h-5 w-5 text-amber-600 mr-1" />
            <div className="text-2xl font-bold text-amber-600">{stats.withIncompleteServers}</div>
          </div>
          <div className="text-sm text-amber-700">Serveurs incomplets</div>
          <div className="text-xs text-amber-600 mt-1">{stats.totalIncompleteServers} serveur(s)</div>
        </div>
        
        <div className="bg-red-50 border border-red-200 rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-red-600">{stats.withNoProbe}</div>
          <div className="text-sm text-red-700">Sans sonde</div>
          <div className="text-xs text-red-600 mt-1">⚠️ Critique</div>
        </div>
        
        <div className="bg-orange-50 border border-orange-200 rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-orange-600">{stats.withUnoptimizedDB}</div>
          <div className="text-sm text-orange-700">BDD non optimisée</div>
          <div className="text-xs text-orange-600 mt-1">⚠️ Important</div>
        </div>
      </div>

      {/* Barre de progression globale */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex justify-between items-center mb-2">
          <h2 className="text-lg font-semibold">Complétion moyenne</h2>
          <span className="text-2xl font-bold">{stats.averageCompletion}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
          <div 
            className={`h-full transition-all duration-500 ${
              stats.averageCompletion === 100 ? 'bg-green-500' :
              stats.averageCompletion >= 80 ? 'bg-blue-500' :
              stats.averageCompletion >= 60 ? 'bg-yellow-500' :
              stats.averageCompletion >= 40 ? 'bg-orange-500' :
              'bg-red-500'
            }`}
            style={{ width: `${stats.averageCompletion}%` }}
          />
        </div>
      </div>

      {/* Filtres et tri */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Filtrer:</span>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Tous les clients</option>
              <option value="complete">✅ Complets uniquement</option>
              <option value="incomplete">⚠️ Incomplets</option>
              <option value="server_incomplete">🖥️ Serveurs incomplets ({stats.withIncompleteServers})</option>
              <option value="critical">🔴 Critiques (sonde/BDD)</option>
            </select>
          </div>
          
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Trier par:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="server_issues">Problèmes serveurs (par défaut)</option>
              <option value="completion">Taux de complétion</option>
              <option value="name">Nom du client</option>
              <option value="alerts">Alertes critiques</option>
            </select>
          </div>
        </div>
      </div>

      {/* Liste détaillée des clients */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold">Détail par client ({sortedClients.length} clients)</h2>
        </div>
        
        <div className="divide-y divide-gray-200">
          {sortedClients.map(client => {
            const badge = getCompletionBadge(client.completion.percentage);
            return (
              <div key={client.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <Link 
                        to={`/client/${client.id}`}
                        className="text-lg font-medium text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        {client.name}
                      </Link>
                      
                      {/* Badge de complétion */}
                      <span className={`px-2 py-1 rounded-full text-xs font-bold border ${badge.class}`}>
                        {badge.text}
                      </span>
                      
                      {/* Icônes serveurs incomplets */}
                      {client.completion.serverAnalysis?.incompleteServers?.length > 0 && (
                        <span className="bg-amber-100 text-amber-800 border border-amber-300 px-2 py-1 rounded-full text-xs font-medium flex items-center">
                          <ServerIcon className="h-3 w-3 mr-1" />
                          {client.completion.serverAnalysis.incompleteServers.length} serveur(s) incomplet(s)
                        </span>
                      )}
                      
                      {/* Alertes critiques serveurs */}
                      {client.completion.serverAnalysis?.criticalIssues?.length > 0 && (
                        <span className="bg-red-100 text-red-800 border border-red-300 px-2 py-1 rounded-full text-xs font-medium flex items-center">
                          <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                          {client.completion.serverAnalysis.criticalIssues.length} critique(s)
                        </span>
                      )}
                      
                      {/* Alertes */}
                      {client.completion.details.hasNoProbe && (
                        <span className="bg-red-100 text-red-800 border border-red-300 px-2 py-1 rounded-full text-xs font-medium">
                          ⚠️ Sonde manquante
                        </span>
                      )}
                      {client.completion.details.hasUnoptimizedDB && (
                        <span className="bg-orange-100 text-orange-800 border border-orange-300 px-2 py-1 rounded-full text-xs font-medium">
                          ⚠️ BDD non optimisée
                        </span>
                      )}
                    </div>
                    
                    {client.description && (
                      <p className="text-sm text-gray-600 mt-1">{client.description}</p>
                    )}
                    
                    {/* Détails de complétion */}
                    <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                      <span>{client.servers?.length || 0} serveur(s)</span>
                      
                      {/* Score de complétion serveurs */}
                      {client.completion.serverAnalysis?.completionScore !== undefined && (
                        <span className={`font-medium ${
                          client.completion.serverAnalysis.completionScore >= 90 ? 'text-green-600' :
                          client.completion.serverAnalysis.completionScore >= 70 ? 'text-blue-600' :
                          client.completion.serverAnalysis.completionScore >= 50 ? 'text-yellow-600' :
                          'text-red-600'
                        }`}>
                          Serveurs: {client.completion.serverAnalysis.completionScore}% complets
                        </span>
                      )}
                      
                      {client.completion.criticalMissing.length > 0 && (
                        <span className="text-red-600">
                          {client.completion.criticalMissing.length} champ(s) requis manquant(s)
                        </span>
                      )}
                      {client.completion.percentage < 100 && (
                        <details className="inline">
                          <summary className="cursor-pointer text-blue-600 hover:text-blue-800">
                            Voir détails
                          </summary>
                          <div className="mt-2 p-2 bg-gray-50 rounded text-xs">
                            <div className="space-y-1">
                              {Object.entries(client.completion.sections).map(([section, data]) => {
                                if (data.percentage < 100 && data.weight > 0) {
                                  return (
                                    <div key={section}>
                                      <span className="font-medium capitalize">{section}:</span> {data.percentage}%
                                      {data.missing.length > 0 && (
                                        <span className="text-gray-500"> - Manquant: {data.missing.slice(0, 2).join(', ')}</span>
                                      )}
                                    </div>
                                  );
                                }
                                return null;
                              })}
                            </div>
                          </div>
                        </details>
                      )}
                    </div>
                  </div>
                  
                  {/* Barre de progression */}
                  <div className="ml-4 w-32">
                    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                      <div 
                        className={`h-full transition-all duration-500 ${
                          client.completion.percentage === 100 ? 'bg-green-500' :
                          client.completion.percentage >= 80 ? 'bg-blue-500' :
                          client.completion.percentage >= 60 ? 'bg-yellow-500' :
                          client.completion.percentage >= 40 ? 'bg-orange-500' :
                          'bg-red-500'
                        }`}
                        style={{ width: `${client.completion.percentage}%` }}
                      />
                    </div>
                    <div className="text-xs text-gray-500 text-center mt-1">
                      {client.completion.percentage}%
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Actions rapides */}
      <div className="mt-6 flex justify-between items-center">
        <Link 
          to="/clients"
          className="text-blue-600 hover:text-blue-800 hover:underline"
        >
          ← Retour à la liste des clients
        </Link>
        
        <div className="flex gap-2">
          <button 
            onClick={() => window.print()}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Imprimer le rapport
          </button>
          <Link 
            to="/version-dashboard"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Voir tableau de bord des versions
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CompletionDashboard;
